import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { getAllListings } from '@/lib/queries/listings'
import type { ListingListItem } from '@/lib/queries/listings'

/**
 * Listings List page
 * Mobile-first design optimized for iPad Mini
 * Based on wireframe: listings-list.html
 */
export default function ListingsList() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft'>('all')
  const [channelFilter, setChannelFilter] = useState<string | null>(null)

  const { data: listings, isLoading, refetch } = useQuery({
    queryKey: ['listings'],
    queryFn: getAllListings,
    // Refetch when component mounts to ensure fresh data after navigation
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    // Ensure data is always fresh (no stale cache)
    staleTime: 0,
    gcTime: 0,
    // Always refetch, even if data exists in cache
    placeholderData: undefined,
  })

  // Refetch when navigating to this page (e.g., after deletion)
  useEffect(() => {
    if (location.pathname === '/listings') {
      refetch()
    }
  }, [location.pathname, refetch])

  // Filter listings based on search query and filters
  const filteredListings = useMemo(() => {
    // If data is not yet loaded, return empty array (loading state handled above)
    if (listings === undefined) return []
    // If listings is null or not an array, return empty array
    if (!Array.isArray(listings)) return []
    // If listings is an empty array, return it
    if (listings.length === 0) return []

    let filtered = listings

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filtered = filtered.filter((listing) => {
        const titleMatch = listing.title?.toLowerCase().includes(query)
        const productTitleMatch = listing.relatedProduct?.title?.toLowerCase().includes(query)
        return titleMatch || productTitleMatch
      })
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((listing) => listing.status === 'synced')
    } else if (statusFilter === 'draft') {
      filtered = filtered.filter((listing) => listing.status === 'not_synced')
    }

    // Channel filter
    if (channelFilter) {
      filtered = filtered.filter((listing) => listing.channel === channelFilter)
    }

    return filtered
  }, [listings, searchQuery, statusFilter, channelFilter])

  // Get unique channels from listings
  const availableChannels = useMemo(() => {
    if (!listings) return []
    const channels = new Set<string>()
    listings.forEach((listing) => {
      if (listing.channel) {
        channels.add(listing.channel)
      }
    })
    return Array.from(channels).sort()
  }, [listings])

  // Helper to format price
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return 'N/A'
    return `£${price.toFixed(2)}`
  }

  // Helper to get listing image URL
  const getListingImageUrl = (listing: ListingListItem): string | null => {
    const product = listing.relatedProduct
    if (!product?.media) return null

    // Try hero image first
    if (product.media.hero?.url) {
      return product.media.hero.url
    }
    // Try first gallery image
    if (product.media.galleryImages && product.media.galleryImages.length > 0) {
      return product.media.galleryImages[0].url || null
    }
    return null
  }

  // Helper to get channel display name
  const getChannelDisplayName = (channelCode: string | null | undefined): string => {
    if (!channelCode) return 'Unknown'
    return channelCode.charAt(0).toUpperCase() + channelCode.slice(1)
  }

  // Helper to get status display
  const getStatusDisplay = (status: string): { text: string; isActive: boolean } => {
    switch (status) {
      case 'synced':
        return { text: 'Active', isActive: true }
      case 'not_synced':
        return { text: 'Draft', isActive: false }
      case 'error':
        return { text: 'Error', isActive: false }
      case 'pending':
        return { text: 'Pending', isActive: false }
      default:
        return { text: status, isActive: false }
    }
  }

  // Helper to check if price is overridden
  const hasPriceOverride = (listing: ListingListItem): boolean => {
    const productRrp = listing.relatedProduct?.rrp
    const listingPrice = listing.price
    // If listing price is N/A (undefined/null), it's not overridden
    if (listingPrice === undefined || listingPrice === null) return false
    // If product RRP is not available, can't determine override
    if (productRrp === undefined || productRrp === null) return false
    // Only overridden if both exist and they're different
    return listingPrice !== productRrp
  }

  // Helper to get product info text
  const getProductInfo = (listing: ListingListItem): string => {
    const product = listing.relatedProduct
    const parts: string[] = []
    
    if (product?.title) {
      parts.push(`Product: ${product.title}`)
    }
    
    if (product?.rrp !== undefined && product.rrp !== null) {
      parts.push(`Base: ${formatPrice(product.rrp)}`)
    } else if (product?.isPrintOnDemand) {
      parts.push('POD Product')
    } else {
      parts.push('Not published')
    }
    
    return parts.join(' • ')
  }

  // Show loading state if data is not yet available (undefined) or if actively fetching
  if (isLoading || listings === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24 flex items-center justify-center">
        <div className="text-gray-500">Loading listings...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Listings
            {listings && (
              <span className="text-lg font-normal text-gray-500 ml-2">
                ({filteredListings.length})
              </span>
            )}
          </h1>
          <input
            type="search"
            placeholder="Search listings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg text-base min-h-[48px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {/* Filter Chips */}
          <div className="flex gap-2 overflow-x-auto mt-3 pb-2 -mx-4 px-4">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                statusFilter === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                statusFilter === 'draft'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Draft
            </button>
            {availableChannels.map((channel) => (
              <button
                key={channel}
                onClick={() => setChannelFilter(channelFilter === channel ? null : channel)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap touch-target-min-44 ${
                  channelFilter === channel
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getChannelDisplayName(channel)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4">
        {filteredListings.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchQuery || statusFilter !== 'all' || channelFilter
                ? 'No listings found matching your filters'
                : 'No listings found'}
            </p>
            {!searchQuery && statusFilter === 'all' && !channelFilter && (
              <Link
                to="/listings/new"
                className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium touch-target-min-44 hover:bg-blue-700 transition-colors"
              >
                Create Your First Listing
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredListings.map((listing) => {
              const imageUrl = getListingImageUrl(listing)
              const statusDisplay = getStatusDisplay(listing.status)
              const hasOverride = hasPriceOverride(listing)
              const productInfo = getProductInfo(listing)

              return (
                <div
                  key={listing._id}
                  onClick={() => navigate(`/listings/${listing._id}`)}
                  className="block bg-white rounded-lg p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow touch-target-min-44 cursor-pointer"
                >
                  {/* Listing Header */}
                  <div className="flex gap-3 mb-3">
                    {/* Listing Image */}
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={listing.title || 'Listing'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-3xl">🖼️</span>
                      )}
                    </div>

                    {/* Listing Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">
                        {listing.title || listing.relatedProduct?.title || 'Untitled Listing'}
                      </h3>
                      <div className="flex gap-2 flex-wrap mb-2 items-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {getChannelDisplayName(listing.channel)}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            statusDisplay.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {statusDisplay.text}
                        </span>
                        {hasOverride && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                            Price Override
                          </span>
                        )}
                      </div>
                      <div className="text-xl font-bold text-blue-600">
                        {formatPrice(listing.price)}
                      </div>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="text-xs text-gray-600">{productInfo}</div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Floating Action Button */}
      <Link
        to="/listings/new"
        className="fixed bottom-24 right-5 w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-light shadow-lg hover:bg-blue-700 transition-colors touch-target-min-44 z-50"
        aria-label="Create new listing"
      >
        +
      </Link>
    </div>
  )
}
